import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Placeholder } from "react-bootstrap";
import { connect } from "react-redux";
import { getCalibrationApi } from "../../../services/CalibrationService";
import GenerateCalibrationReportModal from "./GenerateReport";
import { renderFileDetails } from "../../../utils";
import CancelledCalibration from "./CancelledCalibration";
import EditCalibration from "./EditCalibration";

const CalibrationDetailsLoading = () => {
  return (
    <div>
      <div className="card">
        <div className="card-body">
          <div className="w-100 px-2 mt-3">
            <Placeholder animation="glow">
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
              <Placeholder
                xs={12}
                style={{ height: "20px" }}
                className="rounded my-2"
              />
            </Placeholder>
          </div>
        </div>
      </div>
      <div className="card">
        <div className="card-body">
          <div className="d-flex align-item-center justify-content-between">
            <div className="w-100 px-2">
              <Placeholder animation="glow">
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
              </Placeholder>
            </div>

            <div className="w-100 px-2">
              <Placeholder animation="glow">
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
                <Placeholder
                  xs={12}
                  style={{ height: "20px" }}
                  className="rounded my-2"
                />
              </Placeholder>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function CalibrationDetails({ user }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const resp = await getCalibrationApi(id);
      const respData = resp.data.data;
      setData(respData);
    } catch (error) {
      console.error("Error fetching audit data:", error);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAfterUpdate = (updatedData) => {
    setData((prevData) => ({
      ...prevData,
      calibration_report: updatedData,
      status: "completed", 
    }));
  };  

  const isScheduledDatePassed = () => {
    if (!data?.scheduled_at) return false;
    const scheduledDate = new Date(data.scheduled_at);
    const currentDate = new Date();
    return currentDate >= scheduledDate;
  };

  return (
    <div className="container-fluid">
      {isLoading ? (
        <CalibrationDetailsLoading />
      ) : data ? (
        <div className="row">
          <div className="col-md-4">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm btn-circle"
                    onClick={() => navigate("/calibration")}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                  </button>
                  <h5 className="mb-0 ms-2">Calibration Details</h5>
                </div>
                <EditCalibration data={data} handleAfterUpdate={handleAfterUpdate} />
              </div>
              <div className="card-body">
                <ul className="list-unstyled">
                  <li className="mb-3">
                    <strong>Name:</strong> {data?.name}
                  </li>
                  <li className="mb-3">
                    <strong>Description:</strong> {data.description}
                  </li>
                  <li className="mb-3">
                    <strong>Status:</strong>{" "}
                    {data?.status === "pending" ? (
                      <span className="badge light border-0 badge-warning badge-sm">
                        Pending
                      </span>
                    ) : data?.status === "completed" ? (
                      <span className="badge light border-0 badge-info badge-sm">
                        Completed
                      </span>
                    ) : (
                      <span className="badge light border-0 badge-danger badge-sm">
                        Cancelled
                      </span>
                    )}
                  </li>

                  <li className="mb-3">
                    <strong>Schedule At:</strong> {data?.scheduled_at}
                  </li>
                </ul>
                {data?.calibration_report ? (
                  <>
                    <h5>Report</h5>
                    <div>
                      {renderFileDetails(data?.calibration_report?.report_file)}
                    </div>
                  </>
                ) : (
                  (data?.status !=="cancelled" && isScheduledDatePassed()) &&  (
                  <div className="d-flex justify-content-between justify-content-center">
                    {" "}
                    <GenerateCalibrationReportModal data={data} handleAfterUpdate={handleAfterUpdate} />
                    <CancelledCalibration data={data} setData={setData} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-8">
            <div className="card shadow-sm border-0">
              <div className="card-header">
                <h5 className="mb-2">Instruments</h5>
              </div>
              <div className="card-body">
                <div>
                  <ul className="list-unstyled">
                    {data?.instruments?.map((item, index) => (
                      <li key={index} className="mb-4">
                        <div className="d-flex justify-content-between px-3">
                          <div className="">
                            <div>
                              <strong>Name:</strong> {item?.name}
                            </div>
                            <div>
                              <strong>SL NO.:</strong> {item?.serial_number}
                            </div>
                            <div>
                              <strong>Description:</strong> {item?.description}
                            </div>
                          </div>
                          <p className="">
                            {item?.photo ? (
                              <button
                                className="btn-sm btn btn-info"
                                href={`${process.env.REACT_APP_BASE_URL}${item?.photo}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <i className="fa-solid fa-file-arrow-down me-2"></i>
                                View File
                              </button>
                            ) : (
                              "No file"
                            )}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card shadow-sm border-0">
          <div className="card-body text-center">
            <h4 className="text-muted">
              ⚠️ Calibration details Not Found! <br /> ID {id}
            </h4>
          </div>
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth?.user,
});
export default connect(mapStateToProps)(CalibrationDetails);
